import { useLocation, useNavigate } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  PenLine,
  Repeat2,
  ImageIcon,
  FolderClosed,
} from "lucide-react";
import type { StudioSection } from "@/components/StudioSidebar";

export type StudioActiveTab = "studio" | "saved" | "ai-coach";

interface BottomTabBarProps {
  /** Optional in-page section switcher (Studio passes this so taps on the
   *  current route avoid a router round-trip). When omitted, taps navigate
   *  to the corresponding top-level route. */
  section?: StudioSection;
  onSelectSection?: (section: StudioSection) => void;
}

interface TabDef {
  id: StudioSection;
  path: string;
  label: string;
  icon: LucideIcon;
}

const TABS: TabDef[] = [
  { id: "rewrite", path: "/rewrite", label: "Rewrite", icon: PenLine },
  { id: "repurpose", path: "/repurpose", label: "Repurpose", icon: Repeat2 },
  { id: "images", path: "/images", label: "Images", icon: ImageIcon },
  { id: "workspace", path: "/workspace", label: "Workspace", icon: FolderClosed },
];

const pathToSection = (pathname: string): StudioSection | null => {
  const match = TABS.find((t) => pathname === t.path || pathname.startsWith(`${t.path}/`));
  return match?.id ?? null;
};

const BottomTabBar = ({ section, onSelectSection }: BottomTabBarProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Prefer the explicit section prop (Studio knows its own state). Fall back
  // to deriving the active tab from the pathname so the bar lights up
  // correctly on Settings, etc.
  const activeSection: StudioSection | null = section ?? pathToSection(location.pathname);

  const handleClick = (tab: TabDef) => {
    // Same-route tap inside Studio: switch sections without re-mounting.
    if (onSelectSection && pathToSection(location.pathname) !== null) {
      // Keep the URL aligned with the active section so refresh / share works.
      if (location.pathname !== tab.path) {
        navigate(tab.path);
      }
      onSelectSection(tab.id);
      return;
    }
    navigate(tab.path);
  };

  return (
    <nav
      data-testid="bottom-nav"
      className="fixed bottom-0 left-0 right-0 z-50 flex items-stretch justify-around border-t border-[rgba(15,23,42,0.08)] bg-white lg:hidden"
      style={{
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        minHeight: "calc(56px + env(safe-area-inset-bottom, 0px))",
      }}
    >
      {TABS.map((tab) => {
        const active = activeSection === tab.id;
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleClick(tab)}
            className="relative flex min-h-[56px] min-w-[44px] flex-1 flex-col items-center justify-center gap-0.5 transition-colors max-[360px]:gap-0"
            style={{
              color: active ? "#6366F1" : "#94A3B8",
              WebkitTapHighlightColor: "transparent",
            }}
            aria-label={tab.label}
            aria-current={active ? "page" : undefined}
          >
            {active && (
              <span
                className="absolute top-1 h-1 w-1 rounded-full"
                style={{ background: "linear-gradient(90deg, #6366F1, #3B82F6)" }}
                aria-hidden
              />
            )}
            <Icon
              size={20}
              strokeWidth={active ? 2.2 : 1.8}
              fill={active ? "rgba(99, 102, 241, 0.15)" : "none"}
              style={{ color: active ? "#6366F1" : "#94A3B8" }}
            />
            <span
              className="max-[360px]:sr-only text-[10px] leading-tight"
              style={{
                fontWeight: active ? 600 : 400,
                color: active ? "#6366F1" : "#94A3B8",
              }}
            >
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
};

export default BottomTabBar;
